// src/features/services/sales/api/salesOrderApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Sales-Order Provisioning Pipeline — FE API layer.
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/sales-orders';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Sku {
  sku: string;
  label: string;
  description: string;
  config: Record<string, unknown> | null;
}

export interface WifiNetwork {
  ssid: string;
  pass: string;
  priority?: number;
}

export interface CreateSalesOrderPayload {
  sku: string;
  wifi_networks: WifiNetwork[];
  quantity?: number;
  labels?: string[];
  publish_period_ms?: number;
  sensor_interval_ms?: number;
  sensor_enabled?: boolean;
  config_poll_ms?: number;
  log_flush_ms?: number;
}

export interface LineItem {
  sku: string;
  serial_number: string;
  label: string;
  scope_key: string;
}

export interface ProvisioningConfig {
  serial_number: string;
  wifi_networks: WifiNetwork[];
  wifi_enabled: boolean;
  publish_period_ms: number;
  sensor_interval_ms: number;
  sensor_enabled: boolean;
  config_poll_ms: number;
  log_flush_ms: number;
}

export interface SalesOrder {
  serial_number: string;
  order_scope_key: string;
  id_scope: number | null;
  status: string;
  sku: string;
  label: string;
  quantity: number;
  wifi_network_count?: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateSalesOrderResponse {
  order: SalesOrder;
  line_items: LineItem[];
  provisioning_configs: ProvisioningConfig[];
}

export interface SalesOrderListResponse {
  items: SalesOrder[];
  count: number;
  cursor: string | null;
}

export interface PipelineStatus {
  order: SalesOrder;
  provisioning_config: ProvisioningConfig | null;
  line_items: Array<LineItem & { device_id?: string; hw_id?: string; status: string }>;
  device: { device_id: string; id_scope: number; attributes: Record<string, unknown> } | null;
  latest_telemetry: Array<Record<string, unknown>> | null;
  pipeline_stage: string;
}

// ── API functions ────────────────────────────────────────────────────────────

export async function createSalesOrderApi(
  payload: CreateSalesOrderPayload,
): Promise<CreateSalesOrderResponse> {
  return bff.post<CreateSalesOrderResponse, CreateSalesOrderPayload>(BASE, payload);
}

export async function fetchSalesOrdersApi(
  params?: { status?: string; limit?: number; cursor?: string },
): Promise<SalesOrderListResponse> {
  return bff.get<SalesOrderListResponse>(BASE, params as any);
}

export async function fetchSalesOrderApi(serialNumber: string): Promise<SalesOrder> {
  return bff.get<SalesOrder>(`${BASE}/${encodeURIComponent(serialNumber)}`);
}

export async function fetchSkusApi(): Promise<{ items: Sku[] }> {
  return bff.get<{ items: Sku[] }>(`${BASE}/skus`);
}

export async function fetchPipelineStatusApi(serialNumber: string): Promise<PipelineStatus> {
  return bff.get<PipelineStatus>(`${BASE}/pipeline/${encodeURIComponent(serialNumber)}`);
}
